/**
 * Project Config Gear (V2)
 *
 * Adds a floating gear button to the canvas toolbox that surfaces the
 * project-level settings the user chose on the landing page:
 *
 *   - Style — opens the shared `StyleLibraryModal`; the picked style
 *     becomes the project default for newly-created nodes.
 *   - Aspect Ratio — inline popout (`16:9` / `9:16`); the picked
 *     value becomes the project default for newly-created nodes.
 *   - Autonomy — `ask` (confirm every cost card) or `act` (run the whole
 *     plan unattended).
 *
 * Existing canvas nodes are not modified: their per-node `in_*` values
 * already live in YDoc, and only the workflow-state defaults change.
 *
 * Autonomy is the odd one out: it is not a node default, it governs how the
 * Director behaves on its next run. It had full backend plumbing
 * (`AgentAutonomy`, per-session overrides, a PATCH field) and NO control
 * anywhere in the console — the only way to reach `act` was an API call, so
 * an unattended multi-episode build was impossible from the UI. This row is
 * the missing control. It writes the PROJECT default (no `session_id`);
 * per-session autonomy falls back to it.
 *
 * Feature tag: `canvas-project-config`. NAD trace command:
 *
 *     scripts/nad trace --feature canvas-project-config --last 10m
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MessageCircleQuestion,
  RectangleHorizontal,
  RectangleVertical,
  Settings,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '../../lib/utils'
import { useProjectConfig, useUpdateProjectConfig } from '../../api/hooks'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { ASPECT_RATIOS, type AspectRatio } from '../landing/hero-content'
import {
  AGENT_AUTONOMY_VALUES,
  type AgentAutonomy,
  getStyleOption,
  createNadDebug,
} from '@ponpon/api-client'
import { StyleLibraryModal } from './nodes/shared/StyleLibraryModal'
const log = createNadDebug('canvas-project-config')

const AUTONOMY_LABELS: Record<AgentAutonomy, string> = {
  ask: 'Ask first',
  act: 'Run on its own',
}

const AUTONOMY_HINTS: Record<AgentAutonomy, string> = {
  ask: 'Confirm each cost card',
  act: 'Builds the whole plan unattended',
}

export const ProjectConfigGear = memo(function ProjectConfigGear() {
  const projectId = useProjectId()
  const { data: config } = useProjectConfig(projectId)
  const { mutateAsync: updateConfig } = useUpdateProjectConfig()

  const [isOpen, setIsOpen] = useState(false)
  const [isStyleLibraryOpen, setIsStyleLibraryOpen] = useState(false)
  const [isAspectMenuOpen, setIsAspectMenuOpen] = useState(false)
  const [isAutonomyMenuOpen, setIsAutonomyMenuOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Close popover on outside click / Escape.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target
      if (target instanceof Node && !popoverRef.current?.contains(target)) {
        setIsOpen(false)
        setIsAspectMenuOpen(false)
        setIsAutonomyMenuOpen(false)
      }
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        setIsAspectMenuOpen(false)
        setIsAutonomyMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  const handleToggle = useCallback(() => {
    setIsOpen((open) => {
      const next = !open
      log('gear:toggle', { open: next, has_config: !!config })
      return next
    })
  }, [config])

  const handleOpenStyleLibrary = useCallback(() => {
    log('style:modal_open', { current_style_id: config?.style_id ?? null })
    setIsStyleLibraryOpen(true)
  }, [config?.style_id])

  const handleApplyStyle = useCallback(
    async (styleId: string) => {
      const previous = config?.style_id
      log('style:apply_request', { previous, next: styleId })
      setIsStyleLibraryOpen(false)
      try {
        const next = await updateConfig({ projectId, data: { style_id: styleId } })
        log('style:apply_success', { previous, next: next.style_id })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('style:apply_error', { previous, attempted: styleId, error: message })
        toast.error(`Failed to update style: ${message}`)
      }
    },
    [projectId, config?.style_id, updateConfig],
  )

  const handleOpenAspectMenu = useCallback(() => {
    log('aspect:menu_toggle', {
      current_aspect_ratio: config?.aspect_ratio ?? null,
    })
    setIsAspectMenuOpen((open) => !open)
  }, [config?.aspect_ratio])

  const handleApplyAspect = useCallback(
    async (aspect: AspectRatio) => {
      const previous = config?.aspect_ratio
      log('aspect:apply_request', { previous, next: aspect })
      setIsAspectMenuOpen(false)
      try {
        const next = await updateConfig({ projectId, data: { aspect_ratio: aspect } })
        log('aspect:apply_success', { previous, next: next.aspect_ratio })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('aspect:apply_error', { previous, attempted: aspect, error: message })
        toast.error(`Failed to update aspect ratio: ${message}`)
      }
    },
    [projectId, config?.aspect_ratio, updateConfig],
  )

  const handleOpenAutonomyMenu = useCallback(() => {
    log('autonomy:menu_toggle', { current_autonomy: config?.agent_autonomy ?? null })
    setIsAutonomyMenuOpen((open) => !open)
  }, [config?.agent_autonomy])

  const handleApplyAutonomy = useCallback(
    async (mode: AgentAutonomy) => {
      const previous = config?.agent_autonomy
      log('autonomy:apply_request', { previous, next: mode })
      setIsAutonomyMenuOpen(false)
      try {
        // No session_id: this is the PROJECT default. Session autonomy falls
        // back to it, so it takes effect for any session that has not set its
        // own mode.
        const next = await updateConfig({ projectId, data: { agent_autonomy: mode } })
        log('autonomy:apply_success', { previous, next: next.agent_autonomy })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        log('autonomy:apply_error', { previous, attempted: mode, error: message })
        toast.error(`Failed to update autonomy: ${message}`)
      }
    },
    [projectId, config?.agent_autonomy, updateConfig],
  )

  const styleLabel = useMemo(() => {
    if (!config?.style_id) return 'Loading…'
    return getStyleOption(config.style_id)?.label ?? config.style_id
  }, [config?.style_id])

  const AspectIcon = config?.aspect_ratio === '9:16' ? RectangleVertical : RectangleHorizontal
  const AutonomyIcon = config?.agent_autonomy === 'act' ? Zap : MessageCircleQuestion

  return (
    <>
      <div ref={popoverRef} className="relative">
        <button
          onClick={handleToggle}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group',
          )}
          title="Project settings"
          data-testid="toolbox-project-config"
        >
          <Settings className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        {isOpen && (
          <div
            role="menu"
            data-testid="project-config-popover"
            className="absolute right-full mr-2 top-0 z-30 w-64 rounded-lg border border-border bg-background shadow-xl p-2 space-y-1"
          >
            <div className="px-2 pt-1 pb-1 text-xxs uppercase tracking-wider text-muted-foreground">
              Project defaults
            </div>

            <button
              type="button"
              onClick={handleOpenStyleLibrary}
              disabled={!config}
              data-testid="project-config-style-row"
              className={cn(
                'w-full flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm',
                'text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <span className="text-muted-foreground">Style</span>
              <span
                className="truncate text-foreground"
                data-testid="project-config-style-value"
              >
                {styleLabel}
              </span>
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={handleOpenAspectMenu}
                disabled={!config}
                aria-haspopup="menu"
                aria-expanded={isAspectMenuOpen}
                data-testid="project-config-aspect-row"
                className={cn(
                  'w-full flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm',
                  'text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <span className="text-muted-foreground">Aspect ratio</span>
                <span
                  className="flex items-center gap-1.5 text-foreground"
                  data-testid="project-config-aspect-value"
                >
                  <AspectIcon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                  {config?.aspect_ratio ?? '—'}
                </span>
              </button>

              {isAspectMenuOpen && config && (
                <div
                  role="menu"
                  data-testid="project-config-aspect-menu"
                  className="absolute right-0 top-full mt-1 z-40 w-28 rounded-lg border border-border bg-background shadow-xl p-1"
                >
                  {ASPECT_RATIOS.map((ratio) => {
                    const Icon = ratio === '9:16' ? RectangleVertical : RectangleHorizontal
                    const checked = config.aspect_ratio === ratio
                    return (
                      <button
                        key={ratio}
                        type="button"
                        role="menuitemradio"
                        aria-checked={checked}
                        data-testid={`project-config-aspect-option-${ratio.replace(':', '-')}`}
                        onClick={() => handleApplyAspect(ratio)}
                        className={cn(
                          'w-full flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors cursor-pointer',
                          checked
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <span className="flex items-center gap-1.5">
                          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                          {ratio}
                        </span>
                        {checked && (
                          <svg
                            className="w-3.5 h-3.5 text-primary"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={handleOpenAutonomyMenu}
                disabled={!config}
                aria-haspopup="menu"
                aria-expanded={isAutonomyMenuOpen}
                data-testid="project-config-autonomy-row"
                className={cn(
                  'w-full flex items-center justify-between gap-2 rounded-md px-2 py-2 text-sm',
                  'text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <span className="text-muted-foreground">Autonomy</span>
                <span
                  className="flex items-center gap-1.5 text-foreground"
                  data-testid="project-config-autonomy-value"
                >
                  <AutonomyIcon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                  {config ? AUTONOMY_LABELS[config.agent_autonomy] : '—'}
                </span>
              </button>

              {isAutonomyMenuOpen && config && (
                <div
                  role="menu"
                  data-testid="project-config-autonomy-menu"
                  className="absolute right-0 top-full mt-1 z-40 w-52 rounded-lg border border-border bg-background shadow-xl p-1"
                >
                  {AGENT_AUTONOMY_VALUES.map((mode) => {
                    const Icon = mode === 'act' ? Zap : MessageCircleQuestion
                    const checked = config.agent_autonomy === mode
                    return (
                      <button
                        key={mode}
                        type="button"
                        role="menuitemradio"
                        aria-checked={checked}
                        data-testid={`project-config-autonomy-option-${mode}`}
                        onClick={() => handleApplyAutonomy(mode)}
                        className={cn(
                          'w-full flex items-start justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors cursor-pointer text-left',
                          checked
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <span className="flex items-start gap-1.5">
                          <Icon
                            className="w-3.5 h-3.5 mt-0.5 shrink-0"
                            strokeWidth={1.5}
                            aria-hidden="true"
                          />
                          <span>
                            <span className="block">{AUTONOMY_LABELS[mode]}</span>
                            <span className="block text-xxs text-muted-foreground">
                              {AUTONOMY_HINTS[mode]}
                            </span>
                          </span>
                        </span>
                        {checked && (
                          <svg
                            className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="px-2 pt-1 pb-1 text-xxs text-muted-foreground">
              Style and aspect apply to new nodes; existing nodes are unchanged.
              Autonomy applies to the next Director run.
            </div>
          </div>
        )}
      </div>

      {config && (
        <StyleLibraryModal
          isOpen={isStyleLibraryOpen}
          onClose={() => {
            log('style:modal_close', { applied: false })
            setIsStyleLibraryOpen(false)
          }}
          currentStyleId={config.style_id}
          onApply={handleApplyStyle}
        />
      )}
    </>
  )
})
